/**
 * Printable traceability report (OBRAZAC EV-SLJ), without the HACCP section.
 * Opens in a new window so the operator can Save as PDF from the browser.
 */

export type TraceabilityReportInput = {
  ingredient_name?: string | null;
  internal_lot_code?: string | null;
  supplier_lot?: string | null;
  supplier_name?: string | null;
  planned_kg?: number | null;
  actual_kg?: number | null;
  has_receipt?: boolean;
  is_semi_finished?: boolean;
  produced_by_name?: string | null;
  produced_by?: {
    inferred?: boolean;
    product_name?: string | null;
    inputs?: TraceabilityReportInput[];
  } | null;
  receipt_number?: string | null;
  receipt_date?: string | null;
  expiry_date?: string | null;
  quantity_received_kg?: number | null;
};

export type TraceabilityReport = {
  issued_at?: string;
  batch: {
    batch_id: number;
    mix_code?: string | null;
    production_lot_code?: string | null;
    product_name?: string | null;
    production_date?: string | null;
    batch_status?: string | null;
    final_mass_kg?: number | null;
    packed_pieces?: number | null;
    produced_pieces?: number | null;
    scrap_pieces?: number | null;
    scrap_pct?: number | null;
    scrap_color?: number | null;
    scrap_shape?: number | null;
    mixer_name?: string | null;
    oven_name?: string | null;
    is_semi_finished?: boolean;
  };
  work_order?: { id: number; production_date?: string | null; version?: number | null; status?: string | null } | null;
  recipe?: {
    id: number;
    code?: string | null;
    version?: number | null;
    product_name?: string | null;
    notes?: string | null;
    ingredients: { name: string; baker_percentage?: number | null; is_child_recipe?: boolean; notes?: string | null }[];
  } | null;
  phases?: { name: string; code?: string | null; duration_min?: number | null; temp_c?: number | null; notes?: string | null; machine_name?: string | null }[];
  actual_phases?: {
    name?: string | null;
    code?: string | null;
    start?: string | null;
    end?: string | null;
    duration_min?: number | null;
    temp_c?: number | null;
    notes?: string | null;
  }[];
  inputs: TraceabilityReportInput[];
  product_qa?: {
    recorded?: boolean;
    all_passed?: boolean;
    latest?: {
      control_date?: string | null;
      product_name?: string | null;
      product_code?: string | null;
      defined_weight_kg?: number | null;
      production_date?: string | null;
      production_department?: string | null;
      smell_ok?: boolean | null;
      taste_ok?: boolean | null;
      appearance_ok?: boolean | null;
      weight_kg?: Array<number | null>;
      length_cm?: number | null;
      width_cm?: number | null;
      height_cm?: number | null;
      conforms?: boolean | null;
      label_check_ok?: boolean | null;
      controlled_by?: string | null;
      nonconformity_number?: string | null;
      notes?: string | null;
    } | null;
    checks?: unknown[];
  };
  transfers_out?: {
    id: number;
    number?: string | null;
    date?: string | null;
    from_warehouse?: string | null;
    to_warehouse?: string | null;
    from_code?: string | null;
    to_code?: string | null;
    purpose?: "writeoff" | "finished_goods" | "other" | string | null;
    quantity_kg?: number | null;
    quantity_pieces?: number | null;
  }[];
  dispatches: {
    customer_name?: string | null;
    dispatch_number?: string | null;
    dispatch_date?: string | null;
    lot_code?: string | null;
    quantity?: number | string | null;
  }[];
};

const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString("hr-HR") : "—");
const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString("hr-HR", { dateStyle: "short", timeStyle: "short" }) : "—";
const fmtKg = (v?: number | null) =>
  v == null || Number.isNaN(Number(v)) || Number(v) === 0 ? "—" : `${Number(v).toFixed(3)} kg`;
const lotOf = (inp: TraceabilityReportInput) => {
  const vals = [inp.supplier_lot, inp.internal_lot_code];
  return vals.find((v) => v && !/^R-\d/.test(String(v))) || "—";
};
const primkaOf = (inp: TraceabilityReportInput) => {
  if (inp.is_semi_finished) return "—";
  if (inp.receipt_number && inp.receipt_number !== "normativ pakiranja") return inp.receipt_number;
  return inp.has_receipt === false ? "bez primke" : inp.receipt_number || "—";
};
const missingReceipt = (inp: TraceabilityReportInput): boolean =>
  !inp.is_semi_finished && !inp.receipt_number && inp.has_receipt === false;
const anyMissingReceipt = (inputs: TraceabilityReportInput[]) =>
  flattenReceiptRows(inputs).some(({ inp }) => missingReceipt(inp));
const fmtPieces = (v?: number | null) => (v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v)} kom`);
const purposeLabel = (p?: string | null) =>
  p === "writeoff" ? "Otpis / škart" : p === "finished_goods" ? "Skladište gotovih proizvoda" : "Ostalo";
const fmtOutQty = (t: { quantity_kg?: number | null; quantity_pieces?: number | null }) => {
  const pcs = t.quantity_pieces != null ? fmtPieces(t.quantity_pieces) : null;
  const kg = t.quantity_kg != null && Number(t.quantity_kg) !== 0 ? fmtKg(t.quantity_kg) : null;
  if (pcs && kg) return `${pcs} (${kg})`;
  return pcs || kg || "—";
};
const fmtMark = (v?: boolean | null) => (v == null ? "—" : v ? "+" : "−");
const sensoryScore = (notes: string | null | undefined, key: "miris" | "okus" | "izgled") => {
  const m = (notes || "").match(new RegExp(`${key}\\s+(\\d+(?:[.,]\\d+)?)`, "i"));
  return m ? m[1].replace(",", ".") : null;
};
const fmtSensory = (ok: boolean | null | undefined, notes: string | null | undefined, key: "miris" | "okus" | "izgled") =>
  sensoryScore(notes, key) ?? fmtMark(ok);
const fmtPieceWeight = (kg?: number | null) => {
  if (kg == null || Number.isNaN(Number(kg))) return "—";
  const n = Number(kg);
  if (n > 0 && n < 2) return `${Math.round(n * 1000)} g`;
  return `${n} kg`;
};
const isPieceItem = (name?: string | null) =>
  /kalup|vre[cć]ic|klips|traka|deklaracij|kutij|etiket|naljepnic|palet/i.test(name || "");
const fmtConsumed = (inp: { ingredient_name?: string | null; actual_kg?: number | null; planned_kg?: number | null }) => {
  const v = inp.actual_kg ?? inp.planned_kg;
  if (v == null || Number.isNaN(Number(v))) return "—";
  if (!isPieceItem(inp.ingredient_name)) return fmtKg(v);
  const n = Number(v);
  return `${Number.isInteger(n) ? n : n.toFixed(3).replace(/\.?0+$/, "")} kom`;
};
const packagingLast = (inputs: TraceabilityReportInput[]) => [
  ...inputs.filter((inp) => !isPieceItem(inp.ingredient_name)),
  ...inputs.filter((inp) => isPieceItem(inp.ingredient_name)),
];
const flattenReceiptRows = (inputs: TraceabilityReportInput[]) => {
  const rows: TraceabilityReportInput[] = [];
  const seen = new Set<string>();
  const push = (inp: TraceabilityReportInput) => {
    if (inp.is_semi_finished) {
      (inp.produced_by?.inputs || []).forEach(push);
      return;
    }
    const key = `${inp.ingredient_name || ""}|${lotOf(inp)}|${inp.receipt_number || ""}`;
    if (!seen.has(key)) {
      seen.add(key);
      rows.push(inp);
    }
    (inp.produced_by?.inputs || []).forEach(push);
  };
  inputs.forEach(push);
  return packagingLast(rows).map((inp, i) => ({ idx: String(i + 1), inp }));
};

const CSS = `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #000; background: #f5f5f5; }
  .page { background: #fff; width: 210mm; min-height: 297mm; margin: 10px auto; padding: 14mm 14mm 18mm; box-shadow: 0 1px 6px rgba(0,0,0,0.2); }
  table.header { width: 100%; border-collapse: collapse; margin: 0 0 6px; border-bottom: 3px solid #000; }
  table.header td { border: none; padding: 0 0 8px; vertical-align: bottom; }
  .doctype { font-size: 11px; letter-spacing: 1px; color: #555; font-weight: bold; }
  .header h1 { font-size: 21px; margin: 2px 0 0; }
  .header h2 { font-size: 13px; margin: 3px 0 0; font-weight: normal; color: #333; }
  .meta { text-align: right; font-size: 11px; line-height: 1.55; white-space: nowrap; }
  .lot-badge { display: inline-block; background: #000; color: #fff; padding: 3px 9px; font-weight: bold; font-size: 13px; letter-spacing: 0.5px; }
  h3.section { background: #333; color: #fff; padding: 6px 12px; font-size: 13px; font-weight: bold; margin: 16px 0 0; }
  h4.sub { font-size: 11.5px; font-weight: bold; margin: 12px 0 2px; padding: 3px 0 3px 2px; border-bottom: 1px solid #bbb; }
  table { width: 100%; border-collapse: collapse; margin: 6px 0 8px; font-size: 10px; }
  th { background: #f0f0f0; border: 1px solid #999; padding: 4px 6px; text-align: left; font-weight: bold; vertical-align: bottom; }
  td { border: 1px solid #999; padding: 4px 6px; vertical-align: top; }
  td.c, th.c { text-align: center; }
  td.r, th.r { text-align: right; }
  tr.summary td { background: #f0f0f0; font-weight: bold; }
  table.kv td:first-child { background: #fafafa; font-weight: bold; width: 34%; }
  .note { font-size: 10px; color: #444; font-style: italic; margin: 2px 0 10px; }
  .trace-level { border-left: 4px solid #333; background: #fafafa; padding: 6px 10px; margin: 6px 0; }
  .trace-level strong { display: block; font-size: 11px; margin-bottom: 2px; }
  .trace-level span { font-size: 10px; color: #333; }
  .trace-arrow { text-align: center; color: #888; font-size: 13px; line-height: 1; }
  .footnote { margin-top: 16px; padding-top: 8px; border-top: 1px solid #ccc; font-size: 9px; color: #666; line-height: 1.5; }
  .no-print { margin: 12px auto; width: 210mm; text-align: right; }
  .no-print button { font-size: 13px; padding: 8px 16px; cursor: pointer; }
  @media print {
    body { background: #fff; }
    .page { width: auto; min-height: auto; margin: 0; box-shadow: none; padding: 0; }
    .no-print { display: none; }
    tr, .trace-level { page-break-inside: avoid; }
    h3.section, h4.sub { page-break-after: avoid; }
  }
  @page { size: A4; margin: 14mm 14mm 16mm; }
`;

const kv = (pairs: [string, string][]) =>
  `<table class="kv"><tbody>${pairs
    .map(([k, v]) => `<tr><td>${esc(k)}</td><td>${esc(v)}</td></tr>`)
    .join("")}</tbody></table>`;

const table = (headers: string[], rows: string[][], aligns: ("l" | "c" | "r")[]) => {
  const th = headers
    .map((h, i) => `<th class="${aligns[i] === "l" ? "" : aligns[i]}">${esc(h)}</th>`)
    .join("");
  const body = rows
    .map(
      (row) =>
        `<tr>${row
          .map((c, i) => `<td class="${aligns[i] === "l" ? "" : aligns[i]}">${esc(c)}</td>`)
          .join("")}</tr>`
    )
    .join("");
  return `<table><thead><tr>${th}</tr></thead><tbody>${body}</tbody></table>`;
};

const groupRecipe = (ingredients: NonNullable<TraceabilityReport["recipe"]>["ingredients"]) => {
  const groups: { title: string; lines: typeof ingredients }[] = [];
  const titleFor = (notes?: string | null) => {
    const n = (notes || "").toLowerCase();
    if (n.includes("1. tijesto") || n.includes("1.tijesto")) return "1. tijesto";
    if (n.includes("2. tijesto") || n.includes("2.tijesto")) return "2. tijesto";
    if (n.includes("preljev")) return "Preljev";
    if (n.includes("posip")) return "Posip";
    return "Receptura";
  };
  for (const line of ingredients) {
    const title = titleFor(line.notes);
    const last = groups[groups.length - 1];
    if (!last || last.title !== title) groups.push({ title, lines: [line] });
    else last.lines.push(line);
  }
  return groups;
};

export function buildTraceabilityReportHtml(data: TraceabilityReport): string {
  const b = data.batch;
  const lot = b.production_lot_code || b.mix_code || `#${b.batch_id}`;
  const issued = fmtDate(data.issued_at);
  const prodDate = fmtDate(b.production_date);
  const semiInputs = data.inputs.filter((i) => i.is_semi_finished);
  const rawInputs = data.inputs.filter((i) => !i.is_semi_finished);

  const recipeGroups = data.recipe?.ingredients?.length ? groupRecipe(data.recipe.ingredients) : [];

  return `<!DOCTYPE html>
<html lang="hr">
<head>
  <meta charset="utf-8" />
  <title>Izvještaj o sljedivosti · ${esc(lot)}</title>
  <style>${CSS}</style>
</head>
<body>
  <div class="no-print"><button type="button" onclick="window.print()">Ispis / spremi PDF</button></div>
  <div class="page">
    <table class="header"><tr>
      <td>
        <div class="doctype">Boogie Lab &nbsp;·&nbsp; OBRAZAC EV-SLJ &nbsp;·&nbsp; IZDANJE 01</div>
        <h1>IZVJEŠTAJ O SLJEDIVOSTI PROIZVODNE SERIJE</h1>
        <h2>${esc(b.product_name || "—")}</h2>
      </td>
      <td class="meta">
        <div>Datum proizvodnje: ${esc(prodDate)}</div>
        <div>Datum izdavanja: ${esc(issued)}</div>
        <div style="margin-top:6px"><span class="lot-badge">${esc(lot)}</span></div>
      </td>
    </tr></table>

    <h3 class="section">1. Identifikacija proizvodne serije</h3>
    ${kv([
      ["Proizvod", b.product_name || "—"],
      ["Proizvedeni LOT", lot],
      ["Šifra zamjesa (MixCode)", b.mix_code || "—"],
      ["Receptura", data.recipe ? `${data.recipe.code || `#${data.recipe.id}`}${data.recipe.version != null ? `, verzija ${data.recipe.version}` : ""}` : "nije povezana"],
      ["Datum proizvodnje", prodDate],
      ["Mikser / peć", [b.mixer_name, b.oven_name].filter(Boolean).join(" / ") || "—"],
      ...(b.produced_pieces != null ? ([["Proizvedeno", fmtPieces(b.produced_pieces)]] as [string, string][]) : []),
      ...(b.packed_pieces != null ? ([["Spakirano / odobreno (GP)", fmtPieces(b.packed_pieces)]] as [string, string][]) : []),
      ...(b.scrap_pieces != null
        ? ([
            [
              "Otpis (škart)",
              `${fmtPieces(b.scrap_pieces)}${b.scrap_pct != null ? ` (${Number(b.scrap_pct).toFixed(1).replace(".", ",")} %)` : ""}`,
            ],
          ] as [string, string][])
        : []),
      ["Masa tijesta", fmtKg(b.final_mass_kg)],
      ["Status serije", b.batch_status || "—"],
      ["Radni nalog", data.work_order ? `#${data.work_order.id} (${data.work_order.status || "—"})` : "—"],
    ])}

    <h3 class="section">2. Dijagram sljedivosti</h3>
    <div class="trace-level"><strong>Razina 1 — ulazne sirovine</strong><span>${rawInputs.length} lotova sirovina${anyMissingReceipt(data.inputs) ? " (dio bez primke u sustavu)" : ""}</span></div>
    <div class="trace-arrow">▼</div>
    ${
      semiInputs.length
        ? `<div class="trace-level"><strong>Razina 2 — poluproizvodi</strong><span>${semiInputs
            .map((i) => i.produced_by_name || i.ingredient_name || "polu-proizvod")
            .join(", ")}</span></div><div class="trace-arrow">▼</div>`
        : ""
    }
    <div class="trace-level"><strong>Razina 3 — zamjes</strong><span>${esc(
      [b.mix_code || "—", b.packed_pieces != null ? fmtPieces(b.packed_pieces) : null, fmtKg(b.final_mass_kg)]
        .filter(Boolean)
        .join(" · ")
    )}</span></div>
    <div class="trace-arrow">▼</div>
    <div class="trace-level"><strong>Razina 4 — proizvedeni lot</strong><span>${esc(
      [
        lot,
        b.product_name || "—",
        b.produced_pieces != null ? `${fmtPieces(b.produced_pieces)} proizvedeno` : null,
        b.packed_pieces != null ? `${fmtPieces(b.packed_pieces)} odobreno` : null,
        b.scrap_pieces != null ? `${fmtPieces(b.scrap_pieces)} otpis` : null,
        prodDate,
      ]
        .filter(Boolean)
        .join(" · ")
    )}</span></div>
    ${
      (data.transfers_out || []).length
        ? `<div class="trace-arrow">▼</div><div class="trace-level"><strong>Razina 5 — izlaz gotovog</strong><span>${(data.transfers_out || [])
            .map(
              (t) =>
                `${t.number || "MS"} → ${t.to_warehouse || t.to_code || "skladište"} · ${fmtOutQty(t)} · ${purposeLabel(t.purpose)}`
            )
            .join(" · ")}</span></div>`
        : ""
    }
    ${
      data.dispatches.length
        ? `<div class="trace-arrow">▼</div><div class="trace-level"><strong>Razina ${
            (data.transfers_out || []).length ? "6" : "5"
          } — otprema</strong><span>${data.dispatches
            .map((d) => d.customer_name || d.dispatch_number || "kupac")
            .join(", ")}</span></div>`
        : ""
    }

    <h3 class="section">3. Registar ulaznih sirovina</h3>
    ${
      data.inputs.length === 0
        ? `<p class="note">Nema evidentiranog utroška sirovina za ovaj zamjes.</p>`
        : table(
            ["#", "Sirovina", "Dobavljač", "LOT broj", "Primka", "Utrošeno"],
            packagingLast(data.inputs).flatMap((inp, i) => {
              const main = [
                String(i + 1),
                inp.ingredient_name || "—",
                inp.supplier_name || (inp.is_semi_finished ? "Interni poluproizvod" : "—"),
                lotOf(inp),
                primkaOf(inp),
                fmtConsumed(inp),
              ];
              const nested = (inp.produced_by?.inputs || []).map((sub) => [
                "",
                `↳ ${sub.ingredient_name || sub.produced_by?.product_name || "—"}`,
                sub.supplier_name || (sub.is_semi_finished ? "Interni poluproizvod" : "—"),
                lotOf(sub),
                primkaOf(sub),
                fmtConsumed(sub),
              ]);
              return [main, ...nested];
            }),
            ["c", "l", "l", "l", "l", "r"]
          )
    }
    ${
      data.inputs.some((i) => i.receipt_date || i.expiry_date)
        ? `<h4 class="sub">Prijem i rok trajanja</h4>${table(
            ["#", "Sirovina", "Datum prijema", "Rok trajanja", "Zaprimljeno", "Status"],
            flattenReceiptRows(data.inputs).map(({ idx, inp }) => [
              idx,
              inp.ingredient_name || "—",
              fmtDate(inp.receipt_date),
              fmtDate(inp.expiry_date),
              inp.quantity_received_kg == null
                ? "—"
                : isPieceItem(inp.ingredient_name)
                  ? fmtPieces(inp.quantity_received_kg)
                  : fmtKg(inp.quantity_received_kg),
              inp.has_receipt === false
                ? "Bez primke"
                : inp.receipt_number || inp.receipt_date
                  ? "Pušten"
                  : "—",
            ]),
            ["c", "l", "c", "c", "r", "c"]
          )}`
        : ""
    }

    <h3 class="section">4. Receptura</h3>
    ${
      !data.recipe
        ? `<p class="note">Za ovaj proizvod nije povezana receptura u MES-u.</p>`
        : `${kv([
            ["Šifra", data.recipe.code || `#${data.recipe.id}`],
            ["Verzija", data.recipe.version != null ? String(data.recipe.version) : "—"],
            ["Proizvod", data.recipe.product_name || b.product_name || "—"],
          ])}
          ${recipeGroups
            .map(
              (g) =>
                `<h4 class="sub">${esc(g.title)}</h4>${table(
                  ["#", "Sirovina", "Napomena"],
                  g.lines.map((line, i) => [
                    String(i + 1),
                    line.name,
                    line.notes || "—",
                  ]),
                  ["c", "l", "l"]
                )}`
            )
            .join("")}
          ${data.recipe.notes ? `<p class="note">${esc(data.recipe.notes)}</p>` : ""}`
    }

    <h3 class="section">5. Procesni parametri po fazama</h3>
    ${
      (data.phases || []).length === 0 && (data.actual_phases || []).length === 0
        ? `<p class="note">Procesne faze nisu definirane za ovaj proizvod.</p>`
        : (() => {
            const planned = data.phases || [];
            const actual = data.actual_phases || [];
            const n = Math.max(planned.length, actual.length);
            const rows = Array.from({ length: n }, (_, i) => {
              const p = planned[i];
              const a = actual[i];
              return [
                a?.name || p?.name || p?.code || a?.code || "—",
                p?.duration_min != null ? `${p.duration_min} min` : "—",
                p?.temp_c != null ? `${p.temp_c} °C` : "—",
                p?.machine_name || "—",
                fmtDateTime(a?.start),
                fmtDateTime(a?.end),
                a?.duration_min != null ? `${a.duration_min} min` : "—",
                p?.notes || a?.notes || "—",
              ];
            });
            return `${table(
              ["Faza", "Plan", "T plan", "Stroj", "Početak", "Kraj", "Ostvareno", "Napomena"],
              rows,
              ["l", "r", "r", "l", "c", "c", "r", "l"]
            )}`;
          })()
    }

    ${
      !data.product_qa?.recorded || !data.product_qa.latest
        ? ""
        : `<h3 class="section">6. QA gotovog proizvoda</h3>${(() => {
            const c = data.product_qa.latest;
            const weights = c.weight_kg || [];
            return `${kv([
              ["Datum kontrole", fmtDate(c.control_date)],
              ["Proizvod / šifra", [c.product_name, c.product_code].filter(Boolean).join(" · ") || "—"],
              ["Definirana težina", fmtPieceWeight(c.defined_weight_kg)],
              ["Datum proizvodnje", fmtDate(c.production_date)],
              ["Odjel", c.production_department || "—"],
              ["Miris", fmtSensory(c.smell_ok, c.notes, "miris")],
              ["Okus", fmtSensory(c.taste_ok, c.notes, "okus")],
              ["Izgled", fmtSensory(c.appearance_ok, c.notes, "izgled")],
              ["Težine", [1, 2, 3, 4, 5].map((n, i) => `${n}: ${fmtPieceWeight(weights[i])}`).join(" · ")],
              ["Volumen (cm)", `D ${c.length_cm ?? "—"} · Š ${c.width_cm ?? "—"} · V ${c.height_cm ?? "—"}`],
              ["Zadovoljava", fmtMark(c.conforms)],
              ["Deklaracija", fmtMark(c.label_check_ok)],
              ["Kontrolirao", c.controlled_by || "—"],
              ["Nesukladnost", c.nonconformity_number || "—"],
              ...(c.notes ? ([["Napomena", c.notes]] as [string, string][]) : []),
            ])}`;
          })()}`
    }

    ${
      !(data.transfers_out || []).length
        ? ""
        : `<h3 class="section">7. Izlaz gotovog proizvoda</h3>${table(
            ["Dokument", "Odredište", "Namjena", "Količina"],
            (data.transfers_out || []).flatMap((t) => {
              const main = [
                t.number || "—",
                [t.to_code, t.to_warehouse].filter(Boolean).join(" · ") || "—",
                purposeLabel(t.purpose),
                fmtOutQty(t),
              ];
              if (t.purpose !== "writeoff") return [main];
              const extras: string[][] = [];
              if (data.batch.scrap_shape)
                extras.push(["", "↳ Oblik", "škart zbog oblika", fmtPieces(data.batch.scrap_shape)]);
              if (data.batch.scrap_color)
                extras.push(["", "↳ Boja", "škart zbog boje", fmtPieces(data.batch.scrap_color)]);
              return extras.length ? [main, ...extras] : [main];
            }),
            ["l", "l", "l", "r"]
          )}
          <p class="note">Odobreno ide u skladište gotovih proizvoda; škart u skladište otpisa. Količine su s međuskladišnice (kom iz težine komada).</p>`
    }

    <h3 class="section">8. Otprema</h3>
    ${
      data.dispatches.length === 0
        ? `<p class="note">Serija još nije otpremljena.</p>`
        : table(
            ["Kupac", "Otpremnica", "Datum", "Lot", "Količina"],
            data.dispatches.map((d) => [
              d.customer_name || "—",
              d.dispatch_number || "—",
              fmtDate(d.dispatch_date),
              d.lot_code || lot,
              d.quantity == null ? "—" : String(d.quantity),
            ]),
            ["l", "l", "c", "l", "r"]
          )
    }

    <p class="footnote">
      Izvještaj o sljedivosti izrađen iz Poweriot MES sustava. Obuhvaća sljedivost unatrag
      (proizvedeni lot → ulazne sirovine) za jednu proizvodnu seriju. HACCP kontrolne točke
      nisu dio ovog izvoda.
    </p>
  </div>
</body>
</html>`;
}

/**
 * Mora se pozvati sinkrono iz click handlera. `noopener` u windowFeatures
 * ostavlja tab na about:blank i vraća null — preglednik tad lažno javlja
 * da je pop-up blokiran.
 */
export function openReportWindow(): Window {
  const win = window.open("", "_blank");
  if (!win) {
    throw new Error("Preglednik je blokirao novi prozor. Dozvolite pop-up za izvoz izvještaja.");
  }
  win.document.open();
  win.document.write(
    `<!DOCTYPE html><html lang="hr"><head><meta charset="utf-8"><title>Izvještaj o sljedivosti</title></head>` +
      `<body style="font-family:Arial,sans-serif;padding:24px;color:#333">Priprema izvještaja…</body></html>`
  );
  win.document.close();
  return win;
}

export function writeTraceabilityReport(win: Window, data: TraceabilityReport) {
  win.document.open();
  win.document.write(buildTraceabilityReportHtml(data));
  win.document.close();
  win.opener = null;
  win.focus();
}
